import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'app_version';
const CHECK_INTERVAL = 2 * 60 * 1000; // Check every 2 minutes

export const useVersionCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);

  // Function to check for version updates
  const checkVersion = useCallback(async () => {
    try {
      // Add timestamp to URL to prevent caching
      const timestamp = new Date().getTime();
      const response = await fetch(`/version.json?t=${timestamp}`, {
        // Prevent caching
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch version: ${response.status}`);
      }
      
      const data = await response.json();
      const newVersion = data.version;
      
      // First time checking
      if (!currentVersion) {
        setCurrentVersion(newVersion);
        
        // Get stored version from localStorage
        const storedVersion = localStorage.getItem(STORAGE_KEY);
        
        // First time running the app
        if (!storedVersion) {
          localStorage.setItem(STORAGE_KEY, newVersion);
          return;
        }
        
        // Check if version is different from stored version
        if (storedVersion !== newVersion) {
          setUpdateAvailable(true);
        }
        return;
      }
      
      // Check if version has changed since last check
      if (currentVersion !== newVersion) {
        console.log(`Version change detected: ${currentVersion} -> ${newVersion}`);
        setCurrentVersion(newVersion);
        setUpdateAvailable(true);
      }
    } catch (error) {
      console.warn('Version check failed:', error);
    }
  }, [currentVersion]);

  // Initial check and set up interval
  useEffect(() => {
    // Check immediately on mount
    checkVersion();
    
    // Set up interval for checking version
    const interval = setInterval(checkVersion, CHECK_INTERVAL);
    
    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [checkVersion]);

  // Effect to monitor currentVersion changes
  useEffect(() => {
    if (currentVersion) {
      console.log(`Current version: ${currentVersion}`);
    }
  }, [currentVersion]);

  const handleUpdateConfirm = () => {
    if (currentVersion) {
      localStorage.setItem(STORAGE_KEY, currentVersion);
    }
    window.location.reload();
  };

  const handleUpdateLater = () => {
    setUpdateAvailable(false);
  };

  return {
    updateAvailable,
    handleUpdateConfirm,
    handleUpdateLater,
    checkVersion // Export the check function so it can be called manually if needed
  };
}; 
